import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Login from "./pages/Login";
import Customers from "./pages/Customers";
import CustomerDetail from "./pages/CustomerDetail";
import { api, auth } from "./api";

interface Me {
  id: number;
  email: string;
  tenant_id: number | null;
  role: string;
  is_superuser: boolean;
}

// Auth truth is the server: a token alone (or a spoofed one) isn't enough
// — we hit /api/auth/me and only render the protected tree when the
// backend agrees the session is valid.
function RequireAuth({ children }: { children: JSX.Element }) {
  const loc = useLocation();
  const token = auth.token();
  const me = useQuery<Me>({
    queryKey: ["me"],
    queryFn: () => api.get<Me>("/api/auth/me"),
    enabled: !!token,
    staleTime: 60_000,
    retry: false,
  });

  if (!token) return <Navigate to="/login" replace state={{ from: loc }} />;
  if (me.isLoading) {
    return (
      <div className="grid min-h-screen place-items-center bg-canvas text-sm text-muted">
        Loading…
      </div>
    );
  }
  if (me.isError || !me.data) {
    auth.clear();
    return <Navigate to="/login" replace state={{ from: loc }} />;
  }
  return children;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/"
        element={
          <RequireAuth>
            <Customers />
          </RequireAuth>
        }
      />
      <Route
        path="/customers/:id"
        element={
          <RequireAuth>
            <CustomerDetail />
          </RequireAuth>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
